import { formatNumber } from '@angular/common';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';

@Injectable({
  providedIn: 'root'
})
export class ReportPrintService {

  private printFrame: HTMLIFrameElement | null = null;

  private alignColumnText(value: any): string {
    if (this.isDecimal(value)) return 'right';
    if (this.isValidDate(value)) return 'center';
    if (typeof value === 'string') return 'left';
    if (isNaN(Number(value))) return 'left';
    return 'center';
  }

  private buildBodyRows(rows: any[], headers: any[], depth: number): string {
    if (!rows?.length) return '';
    return rows.map(row => `${this.buildDataRow(row, headers, depth)}${this.buildBodyRows(row.children ?? [], headers, depth + 1)}`).join('');
  }

  private buildCaption(descriptions: string[], filters: string[], colCount: number): string {
    if (!descriptions.length && !filters.length) return '';
    const descriptionHtml = descriptions.map(item => `<span>${this.escapeHtml(item)}</span>`).join('');
    const filterHtml = filters.map(item => `<span>${this.escapeHtml(item)}</span>`).join('');
    return `<tr class="caption-container">
      <th colspan="${colCount}">
        <div class="caption-wrap">
          <div class="caption caption-description">${descriptionHtml}</div>
          <div class="caption caption-filters">${filterHtml}</div>
        </div>
      </th>
    </tr>`;
  }

  private buildDataRow(item: any, headers: any[], depth: number): string {
    const isDetail = item?.type === 'detail';
    const colspan = item?.colspan || 1;
    const fontWeight = isDetail ? 'normal' : 'bold';
    const background = isDetail ? '' : `background-color:${this.getBackgroundColor(depth)};`;
    const cells = headers.map((header, index) => {
      if (!isDetail && colspan > 1 && index > 0 && index < colspan) return '';
      const cellColspan = !isDetail && index === 0 ? colspan : 1;
      const paddingLeft = header.propertyName === 'name' ? depth * 5 : 5;
      const value = item?.[header.propertyName];
      const align = this.alignColumnText(value);
      return `<td colspan="${cellColspan}">
        <div style="padding-left:${paddingLeft}px;text-align:${align};">${this.formatCell(value)}</div>
      </td>`;
    }).join('');

    return `<tr style="font-weight:${fontWeight};${background}">${cells}</tr>`;
  }

  private buildHeaderRow(headers: any[]): string {
    const cells = headers.map(header => {
      const separatorClass = header?.name === '' ? ' class="separator"' : '';
      return `<th${separatorClass}><div>${this.escapeHtml(header?.name ?? '')}</div></th>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }

  buildPrintHtml(dataReport: CustomTableReport, showSignatures: boolean = true): string {
    const headers = dataReport?.headers ?? [];
    const rows = dataReport?.data ?? [];
    const totals: any = dataReport?.totals ?? {};
    const captionDescription = dataReport?.captionDescription ?? [];
    const captionFilters = dataReport?.captionFilters ?? [];
    const colCount = Math.max(headers.length, 1);

    const caption = this.buildCaption(captionDescription, captionFilters, colCount);
    const headerRow = this.buildHeaderRow(headers);
    const bodyRows = this.buildBodyRows(rows, headers, 0);
    const totalsRow = this.buildTotalsRow(headers, totals);
    const signatures = showSignatures ? this.buildSignatures() : '';

    return `<!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Impresión</title>
      <style>${this.printStyles()}</style>
    </head>
    <body>
      <table>
        <thead>
          ${caption}
          ${headerRow}
        </thead>
        <tbody>
          ${bodyRows}
        </tbody>
        <tfoot>
          ${totalsRow}
        </tfoot>
      </table>
      ${signatures}
    </body>
    </html>`;
  }

  private buildSignatures(): string {
    return `<div class="signatures">
      <div class="footer footer-elaboro"><p>Elaboró</p></div>
      <div class="footer footer-obs"><p>Observaciones</p></div>
    </div>`;
  }

  private buildTotalsRow(headers: any[], totals: any): string {
    const cells = headers.map(header => {
      const value = totals?.[header.propertyName];
      return `<td style="font-weight:bold;text-align:right;">${this.formatCell(value)}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private formatCell(value: any): string {
    if (value == null || value === '') return '';
    if (this.isDecimal(value)) {
      return this.escapeHtml(formatNumber(Number(value), 'en-US', '1.2-2'));
    }
    return this.escapeHtml(String(value));
  }

  private getBackgroundColor(depth: number): string {
    const startColor = [200, 200, 200];
    const endColor = [244, 244, 244];
    const r = Math.round(startColor[0] + (endColor[0] - startColor[0]) * (depth / 3));
    const g = Math.round(startColor[1] + (endColor[1] - startColor[1]) * (depth / 3));
    const b = Math.round(startColor[2] + (endColor[2] - startColor[2]) * (depth / 3));
    return `rgb(${r}, ${g}, ${b})`;
  }

  private isDecimal(value: any): boolean {
    const includesDot = value?.toString()?.includes('.');
    if (!includesDot) return false;
    if (isNaN(Number(value))) return false;
    return true;
  }

  private isValidDate(value: any): boolean {
    if (!value || typeof value !== 'string') return false;
    return moment(value, ['DD/MM/YYYY', 'DD-MM-YYYY'], true).isValid();
  }

  printHtml(html: string) {
    this.removePrintFrame();

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('title', 'Impresión');
    iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;min-height:297mm;border:0;background:#fff;';
    this.printFrame = iframe;
    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    const frameDocument = iframe.contentDocument ?? frameWindow?.document;
    if (!frameWindow || !frameDocument) {
      this.removePrintFrame();
      return;
    }

    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();

    const contentHeight = Math.max(
      frameDocument.documentElement?.scrollHeight ?? 0,
      frameDocument.body?.scrollHeight ?? 0,
      1123
    );
    iframe.style.height = `${contentHeight}px`;

    const triggerPrint = () => {
      if (this.printFrame !== iframe) return;
      frameWindow.focus();
      frameWindow.addEventListener('afterprint', () => this.removePrintFrame(), { once: true });
      frameWindow.print();
    };

    const schedulePrint = () => {
      requestAnimationFrame(() => requestAnimationFrame(triggerPrint));
    };

    if (frameDocument.readyState === 'complete') {
      schedulePrint();
      return;
    }

    iframe.addEventListener('load', schedulePrint, { once: true });
  }

  private printStyles(): string {
    return `
      @page {
        margin: 5mm;
        margin-bottom: 8mm;
      }
      @page {
        @bottom-right {
          content: counter(page) " / " counter(pages);
          font-size: 9px;
          font-family: Arial, sans-serif;
          color: #444;
          margin-bottom: 1.5rem;
          margin-right: 1rem;
        }
      }
      html, body {
        margin: 0;
        padding: 0;
        background: #fff;
        font-family: Inter, Arial, sans-serif;
        height: auto;
        overflow: visible;
      }
      @media print {
        html, body {
          height: auto !important;
          overflow: visible !important;
        }
      }
      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: auto;
      }
      thead {
        display: table-header-group;
      }
      tfoot {
        display: table-footer-group;
      }
      tr {
        break-inside: avoid;
      }
      th, td {
        font-size: 9px;
        text-align: center;
        border: 1px solid #e8e8e8;
      }
      th {
        padding: 2px;
        background-color: #b9b9b9;
        font-weight: bold;
      }
      td {
        padding: 2px 2px;
      }
      .caption-container th {
        background: #fff;
        border: 0;
        padding: 5px 10px;
      }
      .caption {
        font-size: 11px;
        font-weight: bold;
      }
      .caption-description {
        display: grid;
        grid-template-columns: 1fr 1fr;
        text-align: left;
      }
      .caption-description span:nth-child(even) {
        text-align: right;
      }
      .caption-filters {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        text-align: left;
      }
      .separator {
        width: 0.8rem;
      }
      .signatures {
        display: flex;
        margin-top: 0;
      }
      .footer {
        background-color: white;
        height: 4rem;
        border: solid 1px #e8e8e8;
        border-width: 0 1px 1px 1px;
        font-size: .7rem;
        padding: 4px;
      }
      .footer-elaboro {
        width: 33.33%;
      }
      .footer-obs {
        width: 66.67%;
      }
    `;
  }

  printTableReport(dataReport: CustomTableReport, showSignatures: boolean = true) {
    const html = this.buildPrintHtml(dataReport, showSignatures);
    this.printHtml(html);
  }

  private removePrintFrame() {
    this.printFrame?.remove();
    this.printFrame = null;
  }
}
